import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetEmailTemplatesQuery } from '@/services/endpoints/campaigns';
import { EmailTemplateResponse } from '@/services/endpoints/campaigns/types';
import { EmailTemplate, EmailTemplateInput } from '../types/emailTemplates';

const USE_EMAIL_TEMPLATES_API = true;

const wait = (delayMs = 160) => new Promise((resolve) => setTimeout(resolve, delayMs));

const formatDateLabel = (rawDate?: string, prefix = 'Updated') => {
  if (!rawDate) {
    return 'N/A';
  }

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);

  return `${prefix} ${formatted}`;
};

const mapTemplateResponseToUi = (item: EmailTemplateResponse): EmailTemplate => ({
  id: item.uuid,
  numericId: item.id,
  name: item.name || 'N/A',
  category: item.category || 'N/A',
  subject: item.subject || 'N/A',
  createdAtLabel: formatDateLabel(item.createdAt, 'Created'),
  updatedAtLabel: formatDateLabel(item.updatedAt, 'Updated'),
});

const createTemplate = async (input: EmailTemplateInput) => {
  await wait();
  const now = new Date().toISOString();
  const next: EmailTemplate = {
    id: `template_${Date.now()}`,
    numericId: Date.now(),
    name: input.name.trim(),
    category: 'Marketing',
    subject: input.subject.trim(),
    createdAtLabel: formatDateLabel(now, 'Created'),
    updatedAtLabel: formatDateLabel(now, 'Updated'),
  };

  return next;
};

const updateTemplate = async (templateId: string, input: EmailTemplateInput): Promise<EmailTemplate> => {
  await wait();
  const now = new Date().toISOString();
  return {
    id: templateId,
    numericId: 0,
    name: input.name.trim() || 'N/A',
    category: 'Marketing',
    subject: input.subject.trim() || 'N/A',
    createdAtLabel: formatDateLabel(now, 'Created'),
    updatedAtLabel: formatDateLabel(now, 'Updated'),
  };
};

type UseEmailTemplatesOptions = {
  skip?: boolean;
};

export const useEmailTemplates = (options?: UseEmailTemplatesOptions) => {
  const workspace = useWorkspace();
  const shouldSkip = !workspace.id || options?.skip;
  const { data, isFetching, isError, error, isUninitialized, refetch } = useGetEmailTemplatesQuery(
    { workspaceId: workspace.id },
    { skip: shouldSkip }
  );
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const normalizedTemplates = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(mapTemplateResponseToUi);
  }, [data]);

  useEffect(() => {
    setTemplates(normalizedTemplates);
  }, [normalizedTemplates]);

  const reload = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const create = useCallback(
    async (input: EmailTemplateInput) => {
      const created = await createTemplate(input);
      setTemplates((prev) => [created, ...prev]);
      return created;
    },
    [setTemplates]
  );

  const update = useCallback(
    async (templateId: string, input: EmailTemplateInput) => {
      const updated = await updateTemplate(templateId, input);

      if (!updated) {
        return undefined;
      }

      setTemplates((prev) => prev.map((item) => (item.id === templateId ? updated : item)));
      return updated;
    },
    [setTemplates]
  );

  const getById = useCallback(async (templateId: string) => templates.find((item) => item.id === templateId), [templates]);

  return {
    templates,
    templatesCount: templates.length,
    isLoading: shouldSkip ? false : isFetching || isUninitialized,
    isError: shouldSkip ? false : isError,
    error,
    reload,
    create,
    update,
    getById,
    isUsingApi: USE_EMAIL_TEMPLATES_API,
  };
};
